import { RequireService } from "@Light/components/app/RequireService";
import {
  useOverlay,
  useOverlayMaxHeight,
} from "@Light/components/page/Overlay";
import type { LocationPlan } from "@Light/services/lightTypes";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { NoRenewalOffers } from "../NoRenewalOffers";
import { RENEWAL_OVERLAY_EXPANDED_MAX_HEIGHT } from "../overlay";
import { RenewRoute } from "../routes";
import { RenewContext, type UseRenew } from "./context";
import type { RenewCustomerState, RenewScreen } from "./types";

export type RenewFlowProps = {
  /** Where the flow is mounted, e.g. "renew"; screens route as `/${basePath}/${path}`. */
  basePath: string;
  screens: RenewScreen[];
  customerState: RenewCustomerState;
  expirationDate: Date;
  earlyTerminationFee: number;
  noOffers: boolean;
  onLogout: (reason?: string) => void;
  currentPlan: LocationPlan;
  transitionDate: Date | null;
  isRecent: boolean;
  justRenewed: boolean;
};

const joinPath = (base: string, path: string): string => `/${base}${path}`;

const isCompletedState = (customerState: RenewCustomerState) =>
  customerState === "already_renewed" || customerState === "renewed_recently";

const entryScreen = (
  screens: RenewScreen[],
  basePath: string,
  pathname: string,
  customerState: RenewCustomerState,
): RenewScreen => {
  if (isCompletedState(customerState)) {
    const successPath: string = RenewRoute.Success;
    return screens.find((screen) => screen.path === successPath) ?? screens[0];
  }
  return (
    screens.find((screen) => joinPath(basePath, screen.path) === pathname) ??
    screens[0]
  );
};

const initialNav = (
  screens: RenewScreen[],
  basePath: string,
  pathname: string,
  customerState: RenewCustomerState,
): UseRenew["nav"] => {
  const entry = entryScreen(screens, basePath, pathname, customerState);
  if (isCompletedState(customerState)) {
    return { stack: [entry.path], cursor: 0 };
  }
  const end = screens.findIndex((screen) => screen.path === entry.path);
  return {
    stack: screens.slice(0, end + 1).map((screen) => screen.path),
    cursor: Math.max(end, 0),
  };
};

export function RenewFlow({
  basePath,
  screens,
  customerState,
  expirationDate,
  earlyTerminationFee,
  noOffers,
  onLogout,
  currentPlan,
  transitionDate,
  isRecent,
  justRenewed,
}: RenewFlowProps) {
  const routerNavigate = useNavigate();
  const { pathname } = useLocation();
  const overlay = useOverlay();
  useOverlayMaxHeight({ expanded: RENEWAL_OVERLAY_EXPANDED_MAX_HEIGHT });
  const seeded = useRef(false);

  const [nav, setNav] = useState<UseRenew["nav"]>(() => {
    const currentPath =
      typeof window === "undefined" ? "" : window.location.pathname;
    return initialNav(screens, basePath, currentPath, customerState);
  });

  const transitionTo = (path: string, swap: () => void): Promise<void> =>
    overlay.transitionExpanded(
      screens.find((screen) => screen.path === path)?.expanded ?? false,
      swap,
    );

  const navigate = (path: string, opts: { replace?: boolean } = {}) => {
    setNav((n) => {
      if (opts.replace) {
        const stack = [...n.stack.slice(0, n.cursor), path];
        return { stack, cursor: stack.length - 1 };
      }
      if (n.stack[n.cursor] === path) return n;
      const visited = n.stack.lastIndexOf(path);
      if (visited >= 0) return { ...n, cursor: visited };
      const stack = [...n.stack.slice(0, n.cursor + 1), path];
      return { stack, cursor: stack.length - 1 };
    });

    routerNavigate(joinPath(basePath, path), {
      replace: opts.replace === true,
    });
  };

  useLayoutEffect(() => {
    if (seeded.current) return;
    seeded.current = true;

    const nav = initialNav(screens, basePath, pathname, customerState);
    const entry =
      screens.find((screen) => screen.path === nav.stack[nav.cursor]) ??
      screens[0];
    setNav(nav);

    routerNavigate(joinPath(basePath, entry.path), { replace: true });

    if (entry.expanded) {
      void overlay.transitionExpanded(true);
    }
  }, [screens, basePath, pathname, customerState, routerNavigate, overlay]);

  useEffect(() => {
    const onPopState = () => {
      const { pathname: nextPath } = window.location;
      if (nav.stack.length === 0) return;
      if (nextPath === joinPath(basePath, nav.stack[nav.cursor])) return;

      const screen = screens.find(
        (candidate) => joinPath(basePath, candidate.path) === nextPath,
      );
      if (!screen) return;

      setNav((n) => {
        const cursor = n.stack.indexOf(screen.path);
        if (cursor >= 0) return { ...n, cursor };
        const stack = [...n.stack.slice(0, n.cursor + 1), screen.path];
        return { stack, cursor: stack.length - 1 };
      });

      void overlay.transitionExpanded(screen.expanded ?? false, undefined, {
        fadeOut: false,
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [nav, screens, basePath, overlay]);

  const next = async () => {
    if (nav.cursor < nav.stack.length - 1) {
      const cursor = nav.cursor + 1;
      const path = nav.stack[cursor];
      await transitionTo(path, () => {
        setNav((n) => ({ ...n, cursor }));
        routerNavigate(joinPath(basePath, path));
      });
      return;
    }

    const path = screens[nav.cursor + 1]?.path;
    if (!path) return;
    await transitionTo(path, () => {
      navigate(path);
    });
  };

  const back =
    nav.cursor > 0
      ? () => {
          const cursor = nav.cursor - 1;
          const path = nav.stack[cursor];
          void transitionTo(path, () => {
            setNav((n) => ({ ...n, cursor }));
            routerNavigate(joinPath(basePath, path), { replace: true });
          });
        }
      : undefined;

  const value: UseRenew = {
    screens,
    nav,
    next,
    back,
    customerState,
    expirationDate,
    earlyTerminationFee,
    noOffers,
    onLogout,
    currentPlan,
    transitionDate,
    isRecent,
  };

  const successPath = joinPath(basePath, RenewRoute.Success);
  const noOffersPath = joinPath(basePath, RenewRoute.NoOffers);
  const redirectToSuccess =
    isCompletedState(customerState) && !justRenewed && pathname !== successPath;

  return (
    <RenewContext.Provider value={value}>
      <RequireService redirect={noOffersPath}>
        {redirectToSuccess ? (
          <Navigate to={successPath} replace />
        ) : (
          <Routes>
            {screens.map((screen) => {
              const Screen = screen.component;
              return (
                <Route
                  key={screen.path}
                  path={screen.path}
                  element={<Screen />}
                />
              );
            })}
          </Routes>
        )}
      </RequireService>
      <Routes>
        <Route
          path={RenewRoute.NoOffers}
          element={
            <NoRenewalOffers
              onDone={() => {
                onLogout("no_renewal_offers_inactive");
              }}
            />
          }
        />
      </Routes>
    </RenewContext.Provider>
  );
}
